/**
 * What a bar, a summary and a milestone look like — in one place.
 *
 * The Gantt, the roadmap and the printed sheet are the same picture of the same plan,
 * and for a while they were not: the Gantt coloured by phase and drew a summary as a
 * bracket while the roadmap coloured by status and drew everything as a rectangle, so a
 * room with both open had to translate. These are the marks; a screen decides where to
 * put them and what gestures they carry.
 */
import clsx from 'clsx'

/** The only fields any of these marks needs from a row. */
export type Marked = {
  id: string
  key: string
  depth: number
  parent_id: string | null
  has_children: boolean
}

/**
 * Each phase its own colour, and every task under it the same one.
 *
 * Status used to pick the colour — green for done, blue for in progress — but the filled
 * part of the bar already says that, and it left every phase looking alike. Colour by
 * phase is what a room full of people can point at: "the orange ones are testing".
 *
 * The "phases" are the top-level rows, unless the plan has a single top-level row
 * standing for the whole project — every plan exported from Project or ProjectLibre
 * does — in which case they are its children. The root itself stays neutral.
 */
export const PHASE_COLOURS = [
  '#2e75b6', '#0e9f6e', '#c026d3', '#ea7a1c', '#7c3aed',
  '#0891b2', '#ca8a04', '#db2777', '#4f46e5', '#65a30d',
]

export function phaseColours<T extends Marked>(rows: T[]): Map<string, string> {
  const byId = new Map(rows.map((r) => [r.id, r]))
  const roots = rows.filter((r) => r.depth === 0)
  // The root that holds most of the plan — an imported plan's single top row — is not
  // a phase, its children are. It used to take a single top-level task added beside
  // that root to turn every phase the same blue, because three roots read as three
  // phases. The strays beside the big root are phases of their own; the root is not.
  const under = (row: T): number =>
    rows.filter((r) => r.parent_id === row.id).reduce((n, kid) => n + 1 + under(kid), 0)
  const big = roots.find((r) => r.has_children && under(r) >= rows.length / 2)
  const isGroup = (r: T): boolean =>
    big ? (r.depth === 1 && r.parent_id === big.id) || (r.depth === 0 && r.id !== big.id) : r.depth === 0
  const ofGroup = new Map<string, string>()
  let n = 0
  for (const r of rows) {
    if (isGroup(r)) {
      ofGroup.set(r.id, PHASE_COLOURS[n % PHASE_COLOURS.length]!)
      n += 1
    }
  }
  const out = new Map<string, string>()
  for (const r of rows) {
    let cursor: T | undefined = r
    let hops = 0
    while (cursor && !ofGroup.has(cursor.id) && hops < 50) {
      cursor = cursor.parent_id ? byId.get(cursor.parent_id) : undefined
      hops += 1
    }
    out.set(r.key, (cursor && ofGroup.get(cursor.id)) ?? 'var(--gantt-epic)')
  }
  return out
}

/**
 * A summary: a thin beam with a tooth at each end, the shape every planner since
 * Project has used so a phase never reads as a task.
 */
export function SummaryBracket({ colour, className }: { colour: string; className?: string }) {
  const tooth = { background: colour, clipPath: 'polygon(0 0, 100% 0, 100% 45%, 50% 100%, 0 45%)' }
  return (
    <div className={clsx('relative h-full', className)}>
      <div className="absolute inset-x-0 top-[3px] h-[6px]" style={{ background: colour }} />
      <div className="absolute top-[3px] left-0 h-[11px] w-[6px]" style={style(tooth)} />
      <div className="absolute top-[3px] right-0 h-[11px] w-[6px]" style={style(tooth)} />
    </div>
  )
}

/** A fresh object each time: React keeps the one it is given, and two bars share none. */
function style(from: { background: string; clipPath: string }) {
  return { ...from }
}

/**
 * A task: its colour at a third strength for what is left, solid for what is done.
 *
 * The per-cent is the length of the fill rather than a number written on the bar,
 * because a bar too short to hold text is the common case and a number that disappears
 * at one zoom level is not a reading of the plan.
 */
export function TaskFill({
  colour,
  done,
  hollow,
  className,
}: {
  colour: string
  done: number
  /** Dates borrowed from children or a sprint: a span nobody committed to. */
  hollow?: boolean
  className?: string
}) {
  return (
    <div
      className={clsx('relative h-full overflow-hidden rounded-[3px]', className)}
      style={{ background: `color-mix(in srgb, ${colour} 34%, transparent)` }}
    >
      <div
        className="absolute inset-y-0 left-0"
        style={{ width: `${Math.max(0, Math.min(100, done))}%`, background: colour }}
      />
      {hollow && (
        <div
          className="absolute inset-0 rounded-[3px] border border-dashed"
          style={{ borderColor: colour }}
        />
      )}
    </div>
  )
}

/** A milestone: a point in time, so a diamond rather than a span. */
export function MilestoneDiamond({
  colour = 'var(--gantt-milestone)',
  size = 14,
  className,
}: {
  colour?: string
  size?: number
  className?: string
}) {
  return (
    <div
      aria-hidden
      className={clsx('rotate-45', className)}
      style={{ width: size, height: size, background: colour }}
    />
  )
}

/** The strip under a chart that says what the colours and the shapes mean. */
export function MarksLegend({
  extra,
  trailing,
  grouping = 'phase',
}: {
  extra?: [string, string][]
  trailing?: React.ReactNode
  /** What the brackets are: a project's phases, or a company's projects. */
  grouping?: 'phase' | 'project'
}) {
  return (
    <div className="flex flex-wrap items-center gap-4 border-t border-[var(--color-rule)] bg-[var(--color-surface-sunk)] px-3 py-1.5 text-[10px] text-[var(--color-ink-dim)]">
      <span className="flex items-center gap-1.5">
        <span aria-hidden className="inline-flex gap-px">
          {PHASE_COLOURS.slice(0, 4).map((c) => (
            <span key={c} className="inline-block h-2.5 w-1.5 rounded-[1px]" style={{ background: c }} />
          ))}
        </span>
        Each {grouping} its own colour · the filled part is how far along it is
      </span>
      {(extra ?? []).map(([label, colour]) => (
        <span key={label} className="flex items-center gap-1.5">
          <span aria-hidden className="inline-block h-2.5 w-4 rounded-[2px]" style={{ background: colour }} />
          {label}
        </span>
      ))}
      {trailing}
    </div>
  )
}
